import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequestJson } from '@/lib/queryClient';
import { clientLogger } from '@/lib/datadog';
import { useToast } from './useToast';
import { PickedImage } from './useImagePicker';

interface UploadProfileImageParams {
  image: PickedImage;
  userId: string;
}

interface UploadURLResponse {
  uploadURL: string;
  objectPath?: string;
}

interface ProfileImageResponse {
  objectPath: string;
  user?: Record<string, any>;
}

interface ProfileImageMutationResult {
  confirmedPath: string;
  updatedUser?: Record<string, any>;
}

/**
 * Hook for uploading and managing profile images
 *
 * This handles the complete flow:
 * 1. Get upload URL from backend
 * 2. Upload image to object storage
 * 3. Set ACL policy for the uploaded image
 */
export function useProfileImage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const uploadProfileImageMutation = useMutation<
    ProfileImageMutationResult,
    Error,
    UploadProfileImageParams
  >({
    mutationFn: async ({ image, userId: _userId }: UploadProfileImageParams) => {
      // Step 1: Get upload URL from backend
      const { uploadURL, objectPath: providedObjectPath } = await apiRequestJson<UploadURLResponse>(
        '/api/objects/upload',
        'POST'
      );

      if (!uploadURL) {
        throw new Error('Failed to get upload URL from server');
      }

      // Step 2: Upload image to Replit object storage
      // The signed URL expects a direct PUT with the file bytes, not multipart/form-data
      // For React Native, we need to read the file and send it as a blob

      // Fetch the file from the URI to get the blob
      const fileResponse = await fetch(image.uri);
      const fileBlob = await fileResponse.blob();

      const headers: HeadersInit = image.mimeType
        ? { 'Content-Type': image.mimeType }
        : {};

      const uploadResponse = await fetch(uploadURL, {
        method: 'PUT',
        body: fileBlob,
        headers,
      });

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        clientLogger.error('Upload error response', { errorText });
        throw new Error(`Upload failed with status ${uploadResponse.status}: ${errorText}`);
      }

      // Extract the object path from the upload URL
      const objectPath = providedObjectPath || new URL(uploadURL).pathname;

      // Step 3: Set ACL policy for the uploaded image (make it public)
      const { objectPath: confirmedPath, user: updatedUser } = await apiRequestJson<ProfileImageResponse>(
        '/api/profile-image',
        'PUT',
        { profileImageURL: objectPath }
      );

      const result: ProfileImageMutationResult = updatedUser
        ? { confirmedPath, updatedUser }
        : { confirmedPath };

      return result;
    },
    onSuccess: ({ confirmedPath, updatedUser }) => {
      if (updatedUser) {
        queryClient.setQueryData(['/api/auth/user'], updatedUser);
      } else {
        queryClient.setQueryData(['/api/auth/user'], (previous: any) =>
          previous ? { ...previous, profileImageUrl: confirmedPath } : previous
        );
      }

      queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });

      toast.show({
        type: 'success',
        title: 'Profile Photo Updated',
        description: 'Your profile photo has been updated successfully.',
      });
    },
    onError: (error: any) => {
      clientLogger.error('Profile image upload error', { error });

      // Check if it's an object storage unavailability error (local dev)
      // Raw API and storage errors can contain sensitive internal details.
      let description = 'Failed to upload profile photo. Please try again.';
      if (error.message?.includes('Object storage is not available')) {
        description = 'Profile photo uploads are not available in local development. This feature will work when deployed to Replit.';
      }

      toast.show({
        type: 'error',
        title: 'Upload Failed',
        description: description,
      });
    },
  });

  return {
    uploadProfileImage: uploadProfileImageMutation.mutate,
    uploadProfileImageAsync: uploadProfileImageMutation.mutateAsync,
    isUploading: uploadProfileImageMutation.isPending,
    uploadProgress: uploadProfileImageMutation.isPending ? undefined : 100,
    error: uploadProfileImageMutation.error,
    isSuccess: uploadProfileImageMutation.isSuccess,
    reset: uploadProfileImageMutation.reset,
  };
}
